/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Degree_ProductVariant as DegreeProductVariant } from '__generated__/graphql-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import useRouter from 'js/lib/useRouter';

import { CloseIcon } from '@coursera/cds-icons';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import type PromotionDetailsV1 from 'bundles/naptimejs/resources/promotionDetails.v1';
import type PromotionEligibilitiesV1 from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import PromotionEligibilityTypes from 'bundles/promotions/common/PromotionEligibilityTypes';
import type { PropsFromUseGetCourseBySlug } from 'bundles/promotions/components/hooks/useGetCourseBySlug';
import { useGetCourseBySlug } from 'bundles/promotions/components/hooks/useGetCourseBySlug';
import useGetDegreeUpsellByCourseId from 'bundles/promotions/components/hooks/useGetDegreeUpsellByCourseId';
import type { PropsFromUseGetDegreeUpsellByCourseId } from 'bundles/promotions/components/hooks/useGetDegreeUpsellByCourseId';
import { useGetMastertracksBySlug } from 'bundles/promotions/components/hooks/useGetMastertracksBySlug';
import type { PropsFromUseGetMastertracksBySlug } from 'bundles/promotions/components/hooks/useGetMastertracksBySlug';
import { useGetSpecializationBySlug } from 'bundles/promotions/components/hooks/useGetSpecializationBySlug';
import type { PropsFromUseGetSpecializationBySlug } from 'bundles/promotions/components/hooks/useGetSpecializationBySlug';
import {
  withAbandonedCartPromotion,
  withPromoCodeFromUrl,
  withPromotionInfo,
} from 'bundles/promotions/components/withPromotionInfo';
import type { MastertrackFragment as Mastertrack } from 'bundles/promotions/queries/__generated__/GetMastertrackBySlugForPromotionBannerQuery';
import type { BannerPromotionData } from 'bundles/promotions/types/Promotions';
import {
  getTrackingData,
  isPromoCodeFromUrl,
  removePromotion,
  shouldRemovePromotion,
} from 'bundles/promotions/utils/productDiscountPromoUtils';
import { getPromotionExpirationDate } from 'bundles/promotions/utils/promotionDateUtils';
import { withProductPageApiData } from 'bundles/promotions/utils/promotionsApiDataUtils';

import _t from 'i18n!nls/promotions';

import 'css!./__styl__/ProductDiscountPromoBanner';

const {
  ELIGIBLE,
  PROMOTION_INACTIVE,
  TOTAL_REDEMPTION_LIMIT_REACHED,
  USER_REDEMPTION_LIMIT_REACHED,
  NOT_ELIGIBLE_FOR_NEW_PAID_LEARNER_PROMOTION,
} = PromotionEligibilityTypes;

const Y_OFFSET_DEADZONE = 20;
const Y_OFFSET_BREAKPOINT = 70; // arbitrary amount for user scrolling a little bit down
const PARENT_EXTRA_CLASS = 'with-product-discount-promo-banner';

type PropsFromCaller = {
  courseSlug?: string;
  s12nSlug?: string;
  masterTrackSlug?: string;
  addParentHeightClass?: (className: string) => void;
  removeParentHeightClass?: (className: string) => void;
};

type PropsFromRouter = {
  router: InjectedRouter;
  currentRoute: string;
};

export type Props = PropsFromCaller &
  PropsFromRouter &
  PropsFromUseGetCourseBySlug &
  PropsFromUseGetSpecializationBySlug & {
    promotionDetails?: PromotionDetailsV1;
    promotionEligibilities?: PromotionEligibilitiesV1;
    masterTrackUnderlyingProductItemId?: string;
    bannerPromotionData: BannerPromotionData;
    askUserToLogIn: boolean;
    shouldShowLegalDisclaimerForOpenPromotion?: boolean;
    promotedMasterTrackOnCDP?: Mastertrack;
  };

type State = {
  isClosed: boolean;
  isHidden: boolean;
};

export const getCurrentRoute = (routes: { name: string }[]) => {
  // weird way to get the current route name by getting the most recently name from routes, as
  // there is no getter to handle this.
  // http://stackoverflow.com/questions/29299717/how-to-get-route-name-in-handler-using-react-route
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return routes[routes.length - 1].name;
};

const defaultStyles = {
  bannerDetails: css`
    height: 100%;
    margin: 0 var(--cds-spacing-600);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  bannerDescription: css`
    text-align: center !important;

    @media (max-width: 768px) {
      font-size: 12px;
      line-height: 14px;
    }
  `,
  legalDisclaimer: css`
    font-size: 12px;
    text-align: center !important;
    margin: 0 0 !important;

    @media (max-width: 768px) {
      font-size: 10px;
      line-height: 1.3;
    }
  `,
  bannerBackground: css`
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;
    background-repeat: no-repeat;
    background-size: cover;
  `,
  bannerLeftBackground: css`
    left: 0;

    @media (max-width: 768px) {
      display: none;
    }
  `,
  bannerRightBackground: css`
    right: 0;

    @media (max-width: 768px) {
      display: none;
    }
  `,
  bannerGradient: css`
    right: 0;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  `,
};

class ProductDiscountPromoBanner extends React.Component<Props, State> {
  state = {
    isClosed: false, // user clicked close, banner is permanently closed for the page session
    isHidden: false, // user scrolls down, banner is temporarily hidden until user scrolls back to the top
  };

  componentDidMount() {
    const closeBanner = this.shouldCloseBanner();
    const { promotionEligibilities, addParentHeightClass, askUserToLogIn, router } = this.props;
    // abandoned cart promo requires user to login for userId verification
    if (askUserToLogIn) {
      router.push({
        pathname: router.location.pathname,
        query: { ...router.location.query, authMode: 'login' },
      });
    }
    // if (user limit reached, total limit reached, expired), remove promoCode so we only show banner with message once
    if (
      promotionEligibilities &&
      promotionEligibilities.eligibilityCode &&
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
      shouldRemovePromotion(promotionEligibilities.eligibilityCode) &&
      promotionEligibilities.promoCodeId
    ) {
      removePromotion(promotionEligibilities.promoCodeId);
    }

    if (closeBanner || !addParentHeightClass) {
      return;
    }

    addParentHeightClass(PARENT_EXTRA_CLASS);
    window.addEventListener('scroll', this.checkScrollPosition);
  }

  componentWillUnmount() {
    this.cleanupBanner();
  }

  cleanupBanner = () => {
    const { removeParentHeightClass } = this.props;

    if (removeParentHeightClass) {
      removeParentHeightClass(PARENT_EXTRA_CLASS);
      window.removeEventListener('scroll', this.checkScrollPosition);
    }
  };

  handleClose = (e: React.MouseEvent<any>): void => {
    e.preventDefault();
    this.closeBanner();
  };

  checkScrollPosition = debounce(() => {
    const closeBanner = this.shouldCloseBanner();
    const { addParentHeightClass, removeParentHeightClass } = this.props;
    const currentOffset = window.pageYOffset;

    // Don't do anything within the range of Y_OFFSET_DEADZONE to Y_OFFSET_BREAKPOINT
    // Since the page height changes when the banner collapses, there is a certain range
    // where the banner can keep flickering as it constantly opens and closes
    if (
      closeBanner ||
      !addParentHeightClass ||
      !removeParentHeightClass ||
      (currentOffset > Y_OFFSET_DEADZONE && currentOffset < Y_OFFSET_BREAKPOINT)
    ) {
      return;
    }

    const isHiddenByOffset = window.pageYOffset > Y_OFFSET_BREAKPOINT;
    const { isHidden } = this.state;
    if (isHiddenByOffset !== isHidden) {
      (isHiddenByOffset ? removeParentHeightClass : addParentHeightClass)(PARENT_EXTRA_CLASS);
      this.setState(() => ({ isHidden: isHiddenByOffset }));
    }
  }, 200);

  closeBanner = (): void => {
    this.setState(() => ({ isClosed: true }), this.cleanupBanner);
  };

  shouldCloseBanner(): boolean {
    const {
      course,
      promotionDetails,
      promotionEligibilities,
      s12n,
      masterTrackUnderlyingProductItemId,
      bannerPromotionData,
    } = this.props;
    const { isClosed } = this.state;

    // TODO confirm logic for not showing banner
    // if (user limit reached, total limit reached, expired), show banner with message to tell user
    // so only close banner for (product not eligible) when we get back an eligibilityCode
    const shouldClose =
      isClosed ||
      !(course || s12n || masterTrackUnderlyingProductItemId) ||
      !bannerPromotionData ||
      !promotionDetails ||
      !promotionEligibilities ||
      promotionEligibilities.isProductNotEligible;
    return shouldClose;
  }

  getLinkRoute(promotionId: string, masterTrackSlug?: string): string {
    if (masterTrackSlug) {
      return `/mastertrack/${masterTrackSlug}`;
    }

    return `/promo/${promotionId}`;
  }

  renderPromoMessage(): React.ReactNode {
    const {
      course,
      promotedMasterTrackOnCDP,
      promotionDetails,
      promotionEligibilities,
      bannerPromotionData,
      shouldShowLegalDisclaimerForOpenPromotion,
    } = this.props;

    if (!promotionDetails || !promotionEligibilities || !bannerPromotionData) {
      return null;
    }

    const { getTitle, getDescription, getLegalDisclaimer, getLinkText, styles } = bannerPromotionData;
    // TODO confirm with design about the messaging for the non 100% free case
    const { discountPercent } = promotionDetails;
    const { eligibilityCode } = promotionEligibilities;
    const isFree = discountPercent && discountPercent >= 100;
    const promotionName = promotionDetails.promotionName || _t('promotion');
    const discountPercentOff = isFree ? (
      _t('free')
    ) : (
      <FormattedMessage message={_t('{discountPercent}% off')} discountPercent={discountPercent} />
    );

    let mainCta;
    let details;

    const legalDisclaimerText = getLegalDisclaimer && getLegalDisclaimer();
    const showLegalDisclaimer =
      (isPromoCodeFromUrl() || shouldShowLegalDisclaimerForOpenPromotion) &&
      eligibilityCode === ELIGIBLE &&
      legalDisclaimerText;

    switch (eligibilityCode) {
      case ELIGIBLE:
        mainCta = getTitle && (
          <FormattedMessage
            message={getTitle()}
            productType={course ? _t('course') : _t('Specialization')}
            andSymbol={<span>&amp;</span>}
            discountPercentOff={discountPercentOff}
          />
        );
        details = (
          <FormattedMessage
            message={getDescription()}
            productType={course ? _t('course') : _t('Specialization')}
            andSymbol={<span>&amp;</span>}
            discountPercentOff={discountPercentOff}
            endDate={getPromotionExpirationDate(promotionDetails.endsAt)}
            seeDetails={
              !isPromoCodeFromUrl() && getLinkText ? (
                <TrackedA
                  trackingName="product_discount_promo_banner_details_click"
                  data={getTrackingData()}
                  css={styles.seeDetailsLinkStyles}
                  className={classNames('button-link')}
                  href={this.getLinkRoute(promotionDetails.id, promotedMasterTrackOnCDP?.slug)}
                  target="_blank"
                  rel="noopener"
                >
                  {getLinkText()}
                </TrackedA>
              ) : null
            }
            {...{ masterTrackName: promotedMasterTrackOnCDP?.name }}
          />
        );

        break;
      // TODO confirm messaging for the below eligibilityCode cases with product/design
      case PROMOTION_INACTIVE:
        mainCta = _t('Promotion Expired');
        details = (
          <FormattedMessage message={_t('Sorry, the {promotionName} has expired.')} promotionName={promotionName} />
        );
        break;
      case TOTAL_REDEMPTION_LIMIT_REACHED:
        mainCta = _t('Promotion Limit Reached');
        details = (
          <FormattedMessage
            message={_t('Sorry, the total redemption limit for the {promotionName} has been reached.')}
            promotionName={promotionName}
          />
        );
        break;
      case USER_REDEMPTION_LIMIT_REACHED:
        mainCta = _t('Promotion Limit Reached');
        details = (
          <FormattedMessage
            message={_t('Sorry, you reached the limit for redeeming the {promotionName}.')}
            promotionName={promotionName}
          />
        );
        break;
      case NOT_ELIGIBLE_FOR_NEW_PAID_LEARNER_PROMOTION:
        mainCta = _t('Sorry, you’re not eligible for this promotion');
        details = (
          <FormattedMessage
            message={_t('This promotion is only available for new learners. You can still enroll without a promotion.')}
            promotionName={promotionName}
          />
        );
        break;
      default:
        mainCta = _t('Invalid Promotion');
        details = (
          <FormattedMessage message={_t('We’re sorry, this promotion is not valid')} promotionName={promotionName} />
        );
        break;
    }
    return (
      <div data-testid="promo-banner-valid-promotion" css={[styles.bannerDetails, defaultStyles.bannerDetails]}>
        <h2 className="banner-main-cta">{mainCta}</h2>
        <p css={[styles.bannerDescription, defaultStyles.bannerDescription]}>{details}</p>
        {showLegalDisclaimer && (
          <p css={[styles.legalDisclaimer, defaultStyles.legalDisclaimer]}>
            <FormattedMessage
              message={legalDisclaimerText}
              endDate={getPromotionExpirationDate(promotionDetails.endsAt)}
            />
          </p>
        )}
      </div>
    );
  }

  render() {
    const { bannerPromotionData } = this.props;
    const { isHidden } = this.state;

    if (this.shouldCloseBanner()) {
      return null;
    }

    const { styles } = bannerPromotionData;

    const wrapperClass = classNames('rc-ProductDiscountPromoBanner', {
      hidden: isHidden,
    });
    const trackingData = getTrackingData();
    return (
      <TrackedDiv
        data-testid="product-discount-promo-banner-div"
        trackingName="product_discount_promo_banner_shown"
        data={trackingData}
        className={wrapperClass}
        data-e2e="promo-discount-banner"
      >
        <div
          css={[styles.bannerLeftBg, defaultStyles.bannerBackground, defaultStyles.bannerLeftBackground]}
          aria-hidden="true"
        />
        <div
          css={[styles.bannerGradient, defaultStyles.bannerBackground, defaultStyles.bannerGradient]}
          aria-hidden="true"
        />
        <div
          css={[styles.bannerRightBg, defaultStyles.bannerBackground, defaultStyles.bannerRightBackground]}
          aria-hidden="true"
        />
        <div className="banner-close vertical-box align-items-absolute-center">
          <TrackedButton
            trackingName="product_discount_promo_banner_close"
            data={trackingData}
            className="button-link"
            onClick={this.handleClose}
          >
            <CloseIcon size="large" data-testid="promo-banner-close-button" color="invert" title={_t('close')} />
          </TrackedButton>
        </div>
        {this.renderPromoMessage()}
      </TrackedDiv>
    );
  }
}

// For unit testing
export { ProductDiscountPromoBanner as BaseComp };

type PropsFromUseGraphql = PropsFromUseGetCourseBySlug &
  PropsFromUseGetSpecializationBySlug &
  PropsFromUseGetMastertracksBySlug &
  PropsFromUseGetDegreeUpsellByCourseId & {
    promotedMasterTrackOnCDP?: Mastertrack;
    masterTrackUnderlyingProductItemId?: string;
  };

const ProductDiscountPromoBannerHOC = compose<Props, PropsFromCaller & PropsFromRouter & PropsFromUseGraphql>(
  withAbandonedCartPromotion(),
  withPromoCodeFromUrl(),
  withPromotionInfo(),
  withProductPageApiData()
)(ProductDiscountPromoBanner);

export default deferToClientSideRender((props: PropsFromCaller) => {
  const router = useRouter();
  const { course } = useGetCourseBySlug(props.courseSlug);
  const { s12n } = useGetSpecializationBySlug(props.s12nSlug);
  const { masterTrack } = useGetMastertracksBySlug({ masterTrackSlug: props.masterTrackSlug });
  const { degreeByCourse } = useGetDegreeUpsellByCourseId({ course });
  const { masterTrack: promotedMasterTrackOnCDP } = useGetMastertracksBySlug({
    masterTrackSlug: degreeByCourse?.slug,
    skip: !(degreeByCourse?.productVariant === DegreeProductVariant.MasterTrack),
  });

  return (
    <ErrorBoundaryWithTagging componentName="ProductDiscountPromoBanner">
      <ProductDiscountPromoBannerHOC
        {...props}
        currentRoute={getCurrentRoute(router.routes)}
        router={router}
        course={course}
        s12n={s12n}
        masterTrack={masterTrack}
        masterTrackUnderlyingProductItemId={masterTrack?.id}
        degreeByCourse={degreeByCourse}
        promotedMasterTrackOnCDP={promotedMasterTrackOnCDP}
      />
    </ErrorBoundaryWithTagging>
  );
});
